import { AuthContextProvider, ServiceEnvironment } from '@streamelements/client-sdk-js';
import { UnauthenticatedError } from '../utils/auth';
import { Destination, DestinationMetadataApiClient, DestinationProviderApiClient } from './DestinationsApiClient';
import { promisefy } from '../utils/promisefy';
import { HTTPRequestInfo } from '../types/obs';

export class TrovoApiClient implements DestinationProviderApiClient<'trovo'> {
  static CLIENT_ID = '9437a0029c94c34acb07ecef33e444bc5333ee7acc780459';
  private authContextProvider: AuthContextProvider;
  private serviceEnvironment: ServiceEnvironment;
  private destinationMetadata: Destination;
  constructor({
    authContextProvider,
    serviceEnvironment,
    destinationMetadata,
  }: {
    authContextProvider: AuthContextProvider;
    serviceEnvironment: ServiceEnvironment;
    destinationMetadata: Destination;
  }) {
    this.authContextProvider = authContextProvider;
    this.serviceEnvironment = serviceEnvironment;
    this.destinationMetadata = destinationMetadata;
  }

  public async getAuthHeaders() {
    const {
      channel: { accessToken },
    } = await this.authContextProvider.getAuthContext();

    return {
      Authorization: `OAuth ${this.destinationMetadata.accessToken || accessToken}`,
      'Client-ID': TrovoApiClient.CLIENT_ID,
    };
  }

  private async makeRequest<T>(
    endpoint: string,
    options: Omit<HTTPRequestInfo, 'url' | 'headers'> & {
      params?: { [k: string]: unknown };
      headers?: Record<string, string>;
    } = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  ): Promise<T> {
    const headers = await this.getAuthHeaders();
    const searchParams = new URLSearchParams();

    if (options.params) {
      Object.entries(options.params).forEach(([key, value]) => {
        if (value === undefined) {
          return;
        }

        searchParams.append(key, `${value}`);
      });
    }

    const response = await promisefy(window.host.httpRequestText)({
      url: `https://open-api.trovo.live/${endpoint}?${searchParams.toString()}`,
      method: options.method || 'GET',
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    console.log('Trovo response', response);

    // if (response.ok) {
    if (response.success) {
      try {
        const json = JSON.parse(response.body);
        return json as T;
      } catch (err) {
        // Api returned 200 but no json was returned
        return undefined as never;
      }
    }

    if (response.statusCode === 401) {
      throw new UnauthenticatedError('Unauthenticated');
    }

    throw new Error(`Failed to fetch ${endpoint}`);
  }

  public async fetchStreamKey() {
    const response = await this.makeRequest<{
      stream_key: string;
    }>('channel').then((r) => {
      return r.stream_key;
    });

    return response;
  }

  public async getStreamingOutputInfo() {
    const response = await this.makeRequest<{
      stream_key: string;
    }>('channel').then((r) => {
      return r.stream_key;
    });

    return {
      streamKey: response,
      serverUrl: 'rtmp://live.trovo.live/app',
    };
  }

  public async updateChannelMetadata({ title, gameId }: { title?: string; gameId?: string }) {
    // https://open-api.trovo.live/openplatform/channels/update
    const response = await this.makeRequest('openplatform/channels/update', {
      method: 'POST',
      params: {
        broadcaster_id: this.destinationMetadata.providerId,
      },
      body: JSON.stringify({
        channel_id: this.destinationMetadata.providerId,
        live_title: title,
        category: gameId,
      }),
    });

    return response;
  }

  public setupStreamRoom: DestinationProviderApiClient<'trovo'>['setupStreamRoom'] = async (params) => {
    const { streamKey, serverUrl } = await this.getStreamingOutputInfo();

    await this.updateChannelMetadata({
      title: params.title,
      gameId: params.categoryId,
    }).catch(console.error);

    const destinationMetadataApiClient = new DestinationMetadataApiClient({
      destinationMetadata: this.destinationMetadata,
      authContextProvider: this.authContextProvider,
      serviceEnvironment: this.serviceEnvironment,
    });

    return destinationMetadataApiClient.updateDestination({
      outputs: this.destinationMetadata.outputs
        .map((output) => {
          output.streamingSettings.streamKey = streamKey;
          output.streamingSettings.serverUrl = serverUrl;
          output.streamingSettings.type = 'rtmp_common';
          output.auxiliaryData = {
            ...output.auxiliaryData,
            isPrimaryOutput: output.auxiliaryData?.isPrimaryOutput ?? false,
            destinationId: this.destinationMetadata.id,
            streamTitle: params.title,
            streamCategory: params.categoryId,
          };
          return output;
        })
        .slice(0, 1),
    });
  };

  public async updateStreamRoom(params: {
    outputId: string;
    title?: string;
    categoryId?: string;
    privacyStatus?: string;
  }): Promise<void> {
    await this.updateChannelMetadata({
      title: params.title,
      gameId: params.categoryId,
    }).catch(console.error);
  }

  public async getCategories(params: { query: string }): Promise<Map<string, string>> {
    const response = (await this.makeRequest<{ category_info: { id: string; name: string }[] }>(
      'openplatform/searchcategory',
      {
        method: 'POST',
        body: JSON.stringify({
          query: params.query,
          limit: 10,
        }),
      }
    )) ?? { category_info: [] };

    return new Map(response.category_info.map((category) => [category.id, category.name]));
  }

  public async getCategoryById(params: { categoryId: string }): Promise<{ id: string; displayName: string } | null> {
    void params;
    return null;
  }

  public async endBroadcast(): Promise<void> {
    void 0;
  }
}
